import { ControlDefinition, INDUSTRIES, PROFILE_FLAGS, } from '../controls.js';
import { CONTROL_LINES } from './control-lines.js';
const EVIDENCE = {
    API: 'api',
    Scan: 'scan',
    Config: 'config',
    Doc: 'document',
    Self: 'self',
};
const INDUSTRY_BY_NAME = Object.fromEntries(Object.entries(INDUSTRIES).map(([k, v]) => [v, k]));
const PROFILE_BY_NAME = Object.fromEntries(Object.entries(PROFILE_FLAGS).map(([k, v]) => [v, k]));
const dash = (v) => (v === '' || v === '—' ? null : v);
/** The 257 controls by id (GV-01 … TP-17), validated. */
export const CONTROL_LIBRARY = Object.fromEntries(CONTROL_LINES.trim()
    .split('\n')
    .map((line) => {
    const [id, name, crit, ev, nist, cis, iso, applies, reg, ks, question] = line
        .split('|')
        .map((s) => s.trim());
    const when = dash(applies);
    const def = {
        name: name,
        domain: id.slice(0, 2),
        criticality: Number(crit),
        best_evidence: EVIDENCE[ev],
        frameworks: { nist_csf: nist, cis: dash(cis), iso_27001: dash(iso) },
        regulation: dash(reg),
        kill_switch: ks === 'KS',
        // An industry, a profile fact, or both ("Financial services · Licensed by BNM").
        applies: Object.fromEntries((when?.split(' · ') ?? []).map((w) => INDUSTRY_BY_NAME[w]
            ? ['industries', [INDUSTRY_BY_NAME[w]]]
            : PROFILE_BY_NAME[w]
                ? ['profile', PROFILE_BY_NAME[w]]
                : (() => {
                    throw new Error(`${id}: "${w}" is neither an industry nor a profile fact`);
                })())),
        question: question,
    };
    return [id, ControlDefinition.parse(def)]; // throws on a malformed line
}));
const NOT_SURE = { value: 'not_sure', label: 'Not sure', implementation: null };
const YES_NO = [
    { value: 'yes', label: 'Yes', implementation: 1 },
    { value: 'partly', label: 'Partly', implementation: 0.5 },
    { value: 'no', label: 'No', implementation: 0 },
    NOT_SURE,
];
/** Questions asked the other way round: "yes" is the bad answer. */
const INVERTED = [
    { value: 'no', label: 'No', implementation: 1 },
    { value: 'some', label: 'Some', implementation: 0.5 },
    { value: 'yes', label: 'Yes', implementation: 0 },
    NOT_SURE,
];
const INVERTED_CONTROLS = new Set([
    'AM-03', 'AM-06', 'EP-02', 'EP-04', 'NS-01', 'NS-05', 'VM-02', 'DP-16', 'EH-08', 'TP-02',
]); // prettier-ignore
const scale = (good, partial, none) => [
    { value: 'in_place', label: good, implementation: 1 },
    { value: 'partly', label: partial, implementation: 0.5 },
    { value: 'not_in_place', label: none, implementation: 0 },
    NOT_SURE,
];
/** Questions that ask who, when or how get answers written for them. */
const CUSTOM = {
    'GV-01': scale('A named director or owner', 'Only our IT person or IT provider', 'No one in particular'),
    'GV-05': scale('We know them, and how long we can cope', 'We know the systems, not how long', 'We have not worked this out'),
    'AM-05': scale('We are alerted to new devices', 'We check the network now and then', 'We do not check'),
    'AM-09': scale('Certified wipe or destruction, with a record', 'We reset or wipe them ourselves', 'We do not wipe them'),
    'IA-06': scale('On their last day', 'Within a week', 'Later, or when we remember'),
    'NS-12': scale('Individual sign-ins (WPA2/WPA3-Enterprise)', 'One shared password (WPA2/WPA3)', 'Open, or an old standard such as WEP'),
    'VM-09': scale('In the last 12 months', '1–3 years ago', 'Never, or more than 3 years ago'),
    'DP-15': scale('Through a secure portal or encrypted messaging', 'By email, with password-protected files', 'By ordinary email, fax or chat apps'),
    'BR-03': scale('In the last 90 days', '3–12 months ago', 'More than a year ago, or never'),
    'BR-10': scale('Within a day', 'A few days', 'More than a week'),
    'IR-05': scale('Our incident response firm or insurer hotline', 'Our IT provider', 'We have not decided'),
    'IR-09': scale('We have a tested manual fallback', 'We have a plan, not tested', 'We could not'),
    'TP-04': scale('We have a tested workaround or second provider', 'We would cope for a while, untested', 'The business would stop'),
}; // prettier-ignore
export function controlAnswers(id) {
    return CUSTOM[id] ?? (INVERTED_CONTROLS.has(id) ? INVERTED : YES_NO);
}
//# sourceMappingURL=controls.js.map